import React, { useRef, useState } from "react";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Upload,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import placeholderSvg from "../../../../assets/svg/placeholder.svg";
import {
  Controller,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormReturn,
} from "react-hook-form";
import { validateDocumentFormat } from '@/utils/validateDocument';

const REPLACE_FILE_LABEL = "Substituir arquivo";

interface CardContractProps<TFieldValues extends FieldValues> {
  formMethods: UseFormReturn<TFieldValues>;
}

export const CardContract = <TFieldValues extends FieldValues>({
  formMethods,
}: CardContractProps<TFieldValues>) => {
  const [files, setFiles] = useState<{ name: string; url: string }[]>([]);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(
    null
  );
  const [zoom, setZoom] = useState<number>(1);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    position: number
  ) => {
    const validFiles = validateDocumentFormat(event);
    if (!validFiles) return
    event.target.value = "";

    const dataTransfer = new DataTransfer();
    validFiles.forEach(file => dataTransfer.items.add(file));
    const validFileList = dataTransfer.files;

    const newFiles = Array.from(validFileList).map((file) => {
      const url = URL.createObjectURL(file);
      return { name: file.name, url, file };
    });

    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles];
      newFiles.forEach((newFile, index) => {
        const replaced = updatedFiles[position + index];
        if (replaced) URL.revokeObjectURL(replaced.url);
        updatedFiles[position + index] = newFile;
      });
      return updatedFiles;
    });

    formMethods.setValue(
      `contract_${position}` as Path<TFieldValues>,
      Array.from(validFileList) as PathValue<TFieldValues, Path<TFieldValues>>,
    ); 
  };

  const handlePreviewClick = (index: number) => {
    setSelectedFileIndex(index);
    setZoom(1);
  };

  const handleZoomIn = () => {
    setZoom((prevZoom) => prevZoom + 0.1);
  };

  const handleZoomOut = () => {
    setZoom((prevZoom) => Math.max(1, prevZoom - 0.1));
  };

  const handleNextFile = () => {
    if (selectedFileIndex !== null && selectedFileIndex < files.length - 1) {
      setSelectedFileIndex(selectedFileIndex + 1);
      setZoom(1);
    }
  };

  const handlePreviousFile = () => {
    if (selectedFileIndex !== null && selectedFileIndex > 0) {
      setSelectedFileIndex(selectedFileIndex - 1);
      setZoom(1);
    }
  };

  const renderFilePreview = (index: number) => {
    if (files[index]) {
      const file = files[index];
      const isPDF = file.name.toLowerCase().endsWith(".pdf");
      const isWord = file.name.toLowerCase().endsWith(".doc") || file.name.toLowerCase().endsWith(".docx");
      const isImage = file.name.toLowerCase().endsWith(".png") || file.name.toLowerCase().endsWith(".webp") || file.name.toLowerCase().endsWith(".jpg") || file.name.toLowerCase().endsWith(".jpeg");

      return (
        <div key={index} className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => handlePreviewClick(index)}
            className="relative flex aspect-square w-full items-center justify-center rounded-md border"
          >
          {isPDF ? (
            <embed
              src={file.url}
              type="application/pdf"
              className="w-full h-full object-cover rounded-md"
            />
          ) : isWord ? (
            <div className="flex flex-col items-center justify-center w-full h-full">
              <span className="text-sm text-muted-foreground mb-2 text-center px-2">
                Não é possível visualizar arquivos Word diretamente aqui.<br />
                <b>O arquivo será enviado normalmente.</b><br />
                Para abrir, faça download abaixo.
              </span>
              <a
                href={file.url}
                download={file.name}
                className="px-3 py-1 bg-primary text-white rounded"
              >
                Baixar {file.name}
              </a>
            </div>
          ) : isImage ? (
            <img
              alt={file.name}
              className="w-full h-full rounded-md object-cover"
              src={file.url}
            />
          ) : (
            <img
              alt={file.name}
              className="w-full h-full rounded-md object-cover"
              src={placeholderSvg}
            />
          )}
          <span className="absolute bottom-2 left-2 text-xs bg-black bg-opacity-50 text-white p-1 rounded">
            {file.name}
          </span>
          </button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full flex items-center gap-1.5"
            onClick={() => inputRefs.current[index]?.click()}
          >
            <RefreshCw className="h-4 w-4" />
            {REPLACE_FILE_LABEL}
          </Button>
          {renderFileInput(index)}
        </div>
      );
    }
    return (
      <label className="flex aspect-square w-full cursor-pointer items-center justify-center rounded-md border border-dashed">
        <Upload className="h-6 w-6 text-muted-foreground" />
        <span className="mt-2 text-sm font-medium text-muted-foreground">
          Carregar um Arquivo
        </span>
        {renderFileInput(index)}
      </label>
    );
  };

  const renderFileInput = (index: number): JSX.Element => (
    <Controller
      name={`contract_${index}` as Path<TFieldValues>}
      control={formMethods?.control}
      render={() => (
        <input
          type="file"
          accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.template,application/rtf,application/vnd.oasis.opendocument.text,image/png,image/webp,image/jpg,image/jpeg,image/bmp,image/gif,image/svg+xml,image/tiff,image/heic,image/avif"
          className="hidden"
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          onChange={(event) => {
            handleFileUpload(event, index);
            // field.onChange(event.target.files);
          }}
        />
      )}
    />
  );

  const renderRemainingFiles = () => {
    if (files.length > 3) {
      const remaining = files.length - 3;
      return (
        <div
          className="relative flex h-full w-full items-center justify-center rounded-md border pointer"
          onClick={() => handlePreviewClick(3)}
        >
          <span className="text-muted-foreground text-lg font-medium">
            +{remaining}
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="overflow-hidden" id="contract">
      <CardHeader>
        <CardTitle>Termos</CardTitle>
        <CardDescription className="flex  justify-between flex-col gap-4">
          Clique em qualquer área para anexar ou visualizar arquivos.
          <Button
            variant="link"
            className="w-[125px]"
            onClick={() => handlePreviewClick(1)}
          >
            Visualizar Arquivos
          </Button>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2">
          {renderFilePreview(0)}

          <div className="grid grid-cols-3 gap-2">
            {renderFilePreview(1)}
            {renderFilePreview(2)}
            {files.length > 3 ? renderRemainingFiles() : renderFilePreview(3)}
          </div>
        </div>
      </CardContent>

      <Dialog
        open={selectedFileIndex !== null}
        onOpenChange={() => setSelectedFileIndex(null)}
      >
        <DialogContent className="bg-white w-full max-w-4xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-4 py-2 flex flex-row items-center justify-between">
            <DialogTitle className="text-lg font-semibold">
              {selectedFileIndex !== null ? files[selectedFileIndex]?.name : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="flex justify-between items-center px-4 py-2 border-b">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousFile}
              disabled={selectedFileIndex === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Anterior
            </Button>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="icon" onClick={handleZoomOut}>
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">
                {Math.round(zoom * 100)}%
              </span>
              <Button variant="outline" size="icon" onClick={handleZoomIn}>
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextFile}
              disabled={selectedFileIndex === files?.length - 1}
            >
              Próximo
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          <div className="flex-1 overflow-auto p-4">
            {selectedFileIndex !== null &&
              files[selectedFileIndex]?.url?.endsWith(".pdf") ? (
              <embed
                src={files[selectedFileIndex]?.url}
                type="application/pdf"
                className="w-full h-full"
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: "top left",
                }}
              />
            ) : (
              <iframe
                src={
                  selectedFileIndex !== null
                    ? files[selectedFileIndex]?.url
                    : ""
                }
                className="w-full h-full"
                title={
                  selectedFileIndex !== null
                    ? files[selectedFileIndex]?.name
                    : ""
                }
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: "top left",
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
